import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Hash } from 'lucide-react'
import api from '@/api/client'
import TableSkeleton from '@/components/TableSkeleton'
import EmptyState from '@/components/EmptyState'

/**
 * F3.UX-5: Hashtag/topic feed page. Reached via /tag/:tag
 * Backend already supports `?tag=` query on /api/posts.
 */
export default function Hashtag() {
  const { tag = '' } = useParams<{ tag: string }>()
  const { t } = useTranslation()
  const cleanTag = tag.replace(/^#/, '').toLowerCase()

  const { data, isLoading } = useQuery({
    queryKey: ['tag', cleanTag],
    queryFn: () => api.get(`/posts?tag=${encodeURIComponent(cleanTag)}&page=1&limit=20`).then(r => r.data),
    enabled: !!cleanTag,
  })

  const posts = data?.posts || []

  return (
    <div className="max-w-2xl mx-auto py-6">
      <header className="mb-6 flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
          <Hash className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold">#{cleanTag}</h1>
          <p className="text-sm text-muted-foreground">
            {isLoading ? t('common.loading') : t('tag.postsCount', { count: posts.length })}
          </p>
        </div>
      </header>

      {isLoading ? (
        <TableSkeleton rows={4} withSearch={false} />
      ) : posts.length === 0 ? (
        <EmptyState
          icon="search"
          title={t('tag.noPosts')}
          description={t('tag.noPostsDesc', { tag: cleanTag })}
        />
      ) : (
        <ul className="space-y-3" aria-label={t('tag.postsFor', { tag: cleanTag })}>
          {posts.map((p: any) => (
            <li
              key={p._id}
              className="rounded-xl border border-border bg-card p-4"
            >
              <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{p.username || p.userId?.username}</span>
                <span>•</span>
                <time dateTime={p.createdAt}>{new Date(p.createdAt).toLocaleDateString()}</time>
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">{p.message}</p>
              {p.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.tags.map((t: string) => (
                    <a
                      key={t}
                      href={`/tag/${encodeURIComponent(t)}`}
                      className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      #{t}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
